import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import FlagIcon from '@mui/icons-material/Flag';
import HandymanIcon from '@mui/icons-material/Handyman';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import * as quoteApi from './quoteApi';
import SchematicViewer from './SchematicViewer';
import { FlagCategory, LabourResult, MountingMethod, Quote, QuoteLine, SortDir, displayTagRef, sortBy } from './quoteApi';

const FALLBACK_MOUNTING: MountingMethod[] = [
  { id: null, key: 'Fittted to DIN rail', label: 'Fitted to DIN rail', seconds: 200, seeded: true, no_connections: false },
  { id: null, key: 'Plug-in/clip-in', label: 'Plug-in/clip-in', seconds: 200, seeded: true, no_connections: false },
  { id: null, key: 'Screw mounted component (2 screws)', label: 'Screw mounted component (2 screws)', seconds: 360, seeded: true, no_connections: false },
  { id: null, key: 'Screw mounted component (4 screws)', label: 'Screw mounted component (4 screws)', seconds: 480, seeded: true, no_connections: false },
  { id: null, key: 'Through Hole', label: 'Through Hole', seconds: 200, seeded: true, no_connections: false },
  { id: null, key: 'Fixing DIN rail/Trunking', label: 'Fixing DIN rail/Trunking', seconds: 300, seeded: true, no_connections: false },
  { id: null, key: 'Terminals (inc. labels)', label: 'Terminals (inc. labels)', seconds: 25, seeded: true, no_connections: false },
  { id: null, key: 'Terminal Accessary', label: 'Terminal Accessory', seconds: 10, seeded: true, no_connections: false },
  { id: null, key: 'Large Label', label: 'Large Label', seconds: 260, seeded: true, no_connections: false },
  { id: null, key: 'Fastener', label: 'Fastener', seconds: 0, seeded: true, no_connections: false },
];

const mountingLabel = (methods: MountingMethod[], value?: string | null) => {
  if (!value) return '—';
  const match = methods.find((item) => item.key === value || item.label === value);
  return match?.label || value;
};

const mountingValue = (methods: MountingMethod[], value?: string | null) => {
  if (!value) return '';
  const match = methods.find((item) => item.key === value || item.label === value);
  return match?.key || value;
};

const SUGGESTED_COLOR = '#c05621';
const tableHeadCell = { whiteSpace: 'nowrap' as const };

const suggestedFields = (line?: Partial<QuoteLine> | null): string[] => {
  const raw = line?.labour_inputs?.suggested;
  return Array.isArray(raw) ? raw.map(String) : [];
};

interface LabourViewProps {
  quote: Quote;
  onQuoteChange: (quote: Quote) => void;
}

const LabourView: React.FC<LabourViewProps> = ({ quote, onQuoteChange }) => {
  const [lines, setLines] = useState<QuoteLine[]>([]);
  const [labour, setLabour] = useState<LabourResult | null>(null);
  const [selected, setSelected] = useState<QuoteLine | null>(null);
  const [draft, setDraft] = useState<Partial<QuoteLine>>({});
  const [popup, setPopup] = useState<'labour' | 'crawl' | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [flagCats, setFlagCats] = useState<FlagCategory[]>([]);
  const [locationDraft, setLocationDraft] = useState('');
  const [schematic, setSchematic] = useState<quoteApi.SchematicMeta | null>(null);
  const [schematicPage, setSchematicPage] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [mountingMethods, setMountingMethods] = useState<MountingMethod[]>(FALLBACK_MOUNTING);
  const [sortKey, setSortKey] = useState(quote.source === 'erp_upload' ? 'ref' : 'tag');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [checked, setChecked] = useState<number[]>([]);
  const [bulkMount, setBulkMount] = useState('');
  const [bulkLocation, setBulkLocation] = useState('');
  const locked = quote.status === 'finalised';
  const excelImport = quote.source === 'erp_upload';

  const refresh = async (nextQuote?: Quote) => {
    const q = nextQuote || quote;
    const [lineData, calc, flags] = await Promise.all([
      quoteApi.listLines(q.id),
      quoteApi.calculateLabour(q.id),
      quoteApi.listFlagCategories(),
    ]);
    const active = lineData.filter((l) => !l.is_deleted);
    setLines(active);
    setLabour(calc);
    setFlagCats(flags);
    if (calc.quote) onQuoteChange(calc.quote);
    setSelected((prev) => {
      const next = prev ? active.find((l) => l.id === prev.id) || null : null;
      if (next) setDraft(next);
      return next;
    });
  };

  useEffect(() => {
    refresh().catch((err) => setError(err.message || 'Failed to calculate labour'));
  }, [quote.id]);

  useEffect(() => {
    quoteApi.listMountingMethods().then(setMountingMethods).catch(() => undefined);
  }, []);

  const saveProject = async (fields: Partial<Quote>) => {
    const updated = await quoteApi.updateQuote(quote.id, fields);
    onQuoteChange(updated);
    await refresh(updated);
  };

  const openPopup = (line: QuoteLine, kind: 'labour' | 'crawl') => {
    setSelected(line);
    setDraft(line);
    setPopup(kind);
  };

  const handleSaveLabour = async () => {
    if (!selected) return;
    setBusy(true);
    setError('');
    try {
      const inputs: Record<string, any> = {
        ...selected.labour_inputs,
        conn_20a: Number(draft.labour_inputs?.conn_20a ?? draft.connections ?? 0),
        conn_50a: Number(draft.labour_inputs?.conn_50a || 0),
        conn_167a: Number(draft.labour_inputs?.conn_167a || 0),
        conn_gt167: Number(draft.labour_inputs?.conn_gt167 || 0),
        twisted: Number(draft.labour_inputs?.twisted || 0),
        multicore: Number(draft.labour_inputs?.multicore || 0),
        mounting_not_required: Boolean(draft.labour_inputs?.mounting_not_required),
        no_wire: Boolean(draft.labour_inputs?.no_wire),
      };
      const suggested = suggestedFields(selected).filter((field) => {
        if (field === 'mounting_method' && draft.mounting_method !== selected.mounting_method) return false;
        if (field === 'mounting_location' && draft.mounting_location !== selected.mounting_location) return false;
        if (field === 'connections' && Number(inputs.conn_20a || 0) !== Number(selected.connections || 0)) return false;
        return true;
      });
      const tagRef = displayTagRef({ ...selected, ...draft });
      await quoteApi.updateLine(quote.id, selected.id, {
        mounting_method: inputs.mounting_not_required ? null : draft.mounting_method,
        mounting_location: draft.mounting_location,
        ...(excelImport ? { ref: tagRef || null, tag: tagRef || null } : {}),
        connections: inputs.no_wire ? 0 : Number(inputs.conn_20a || 0),
        labour_inputs: { ...inputs, suggested, conn_20a: inputs.no_wire ? 0 : Number(inputs.conn_20a || 0) },
        flags: draft.flags || [],
      } as Partial<QuoteLine>);
      await refresh();
      setPopup(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to save labour');
    } finally {
      setBusy(false);
    }
  };

  const openSchematic = async (line: QuoteLine) => {
    try {
      const meta = await quoteApi.getSchematicMeta(quote.id, line.id);
      setSchematic(meta);
      setSchematicPage(meta.pages[0] || null);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'No schematic for this TAG');
    }
  };

  const totals = labour || quote.labour;
  const gaps = labour?.labour_gaps || quote.labour_gaps;
  const locations = quote.mounting_locations || [];

  const visible = useMemo(() => {
    const filtered = lines.filter((line) => {
      if (!search) return true;
      const hay = [line.tag, line.ref, line.description, line.mounting_method, line.mounting_location, line.erp_number]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(search.toLowerCase());
    });
    return sortBy(filtered, sortKey, sortDir);
  }, [lines, search, sortKey, sortDir]);

  const toggleSort = (key: string) => {
    if (sortKey === key) setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const allVisibleChecked = visible.length > 0 && visible.every((line) => checked.includes(line.id));

  const applyBulk = async () => {
    if (!checked.length) return;
    setBusy(true);
    try {
      await quoteApi.updateLinesBulk(quote.id, checked, {
        mounting_method: bulkMount || undefined,
        mounting_location: bulkLocation || undefined,
      });
      setChecked([]);
      setBulkMount('');
      setBulkLocation('');
      await refresh();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Bulk update failed');
    } finally {
      setBusy(false);
    }
  };

  const refLabel = excelImport ? 'Ref' : 'Tag';

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>{error}</Alert>}
      {gaps && !gaps.ready && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Required before Review:
          {gaps.quote.length > 0 ? ` ${gaps.quote.join(', ')}.` : ''}
          {gaps.lines.mounting_method ? ` ${gaps.lines.mounting_method} lines missing mounting method.` : ''}
          {gaps.lines.mounting_location ? ` ${gaps.lines.mounting_location} lines missing location.` : ''}
          {gaps.lines.connections ? ` ${gaps.lines.connections} lines missing connections.` : ''}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ px: 1.25, py: 1, mb: 1 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">Locations</Typography>
          {locations.map((loc) => (
            <Chip
              key={loc}
              size="small"
              label={loc}
              onDelete={locked ? undefined : () => saveProject({ mounting_locations: locations.filter((item) => item !== loc) })}
            />
          ))}
          <TextField
            size="small"
            placeholder="Add location"
            value={locationDraft}
            disabled={locked}
            onChange={(e) => setLocationDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && locationDraft.trim()) {
                saveProject({ mounting_locations: [...locations, locationDraft.trim()] });
                setLocationDraft('');
              }
            }}
            sx={{ width: 140 }}
          />
        </Box>
      </Paper>

      {totals && (
        <Box sx={{ display: 'flex', gap: 0.75, mb: 1, flexWrap: 'wrap' }}>
          <Tooltip title="Mechanical + electrical + drilling + project hours, before test / material / H&B uplifts.">
            <Chip size="small" label={`Base ${totals.base_hrs.toFixed(2)} hrs`} />
          </Tooltip>
          <Tooltip title="Base after compounding Test, Material management, and H&B allowances.">
            <Chip size="small" color="info" variant="outlined" label={`After allowances ${totals.after_allowances_hrs.toFixed(2)} hrs`} />
          </Tooltip>
          <Tooltip title="Quoted labour total after volume factor (Batch 80% / Standard 100% / Prototype 150%).">
            <Chip size="small" color="primary" label={`Factored ${totals.final_hrs.toFixed(2)} hrs`} />
          </Tooltip>
          {totals.assembly_confidence != null && (
            <Chip size="small" variant="outlined" label={`Assembly ${totals.assembly_confidence}%`} />
          )}
          <Chip size="small" variant="outlined" label={`Mech ${totals.mechanical_hrs.toFixed(2)}`} />
          <Chip size="small" variant="outlined" label={`Elec ${totals.electrical_hrs.toFixed(2)}`} />
          <Chip size="small" variant="outlined" label={`Drill ${totals.drilling_hrs.toFixed(2)}`} />
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField size="small" placeholder="Filter lines…" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 200, flex: 1 }} />
        <FormControl size="small" sx={{ minWidth: 200 }} disabled={locked || !checked.length}>
          <InputLabel>Bulk mounting</InputLabel>
          <Select value={bulkMount} label="Bulk mounting" onChange={(e) => setBulkMount(e.target.value)}>
            {mountingMethods.map((m) => <MenuItem key={m.key} value={m.key}>{m.label}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }} disabled={locked || !checked.length}>
          <InputLabel>Bulk location</InputLabel>
          <Select value={bulkLocation} label="Bulk location" onChange={(e) => setBulkLocation(e.target.value)}>
            {locations.map((loc) => <MenuItem key={loc} value={loc}>{loc}</MenuItem>)}
          </Select>
        </FormControl>
        <Button size="small" variant="contained" disabled={locked || busy || !checked.length || (!bulkMount && !bulkLocation)} onClick={applyBulk}>
          Apply to {checked.length || 0}
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={locked || busy || !checked.length}
          onClick={async () => {
            setBusy(true);
            try {
              await quoteApi.updateLinesBulk(quote.id, checked, { no_wire: true, connections: 0 });
              setChecked([]);
              await refresh();
            } catch (err: any) {
              setError(err.response?.data?.detail || err.message || 'Bulk no-wire failed');
            } finally {
              setBusy(false);
            }
          }}
        >
          Bulk no-wire
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 260px)' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={allVisibleChecked}
                  indeterminate={checked.length > 0 && !allVisibleChecked}
                  onChange={(e) => setChecked(e.target.checked ? visible.map((line) => line.id) : [])}
                />
              </TableCell>
              <TableCell sx={tableHeadCell}>
                <TableSortLabel active={sortKey === (excelImport ? 'ref' : 'tag')} direction={sortKey === (excelImport ? 'ref' : 'tag') ? sortDir : 'asc'} onClick={() => toggleSort(excelImport ? 'ref' : 'tag')}>
                  {refLabel}
                </TableSortLabel>
              </TableCell>
              <TableCell sx={tableHeadCell}>
                <TableSortLabel active={sortKey === 'description'} direction={sortKey === 'description' ? sortDir : 'asc'} onClick={() => toggleSort('description')}>Description</TableSortLabel>
              </TableCell>
              <TableCell sx={tableHeadCell}>
                <TableSortLabel active={sortKey === 'mounting_method'} direction={sortKey === 'mounting_method' ? sortDir : 'asc'} onClick={() => toggleSort('mounting_method')}>Mounting</TableSortLabel>
              </TableCell>
              <TableCell sx={tableHeadCell}>
                <TableSortLabel active={sortKey === 'mounting_location'} direction={sortKey === 'mounting_location' ? sortDir : 'asc'} onClick={() => toggleSort('mounting_location')}>Location</TableSortLabel>
              </TableCell>
              <TableCell sx={tableHeadCell} align="right">Conn</TableCell>
              <TableCell sx={tableHeadCell} align="right">Hrs</TableCell>
              <TableCell sx={tableHeadCell} align="right">Conf</TableCell>
              <TableCell sx={tableHeadCell} align="center">Labour</TableCell>
              <TableCell sx={tableHeadCell} align="center">Crawl</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((line) => (
              <TableRow key={line.id} hover selected={checked.includes(line.id)}>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={checked.includes(line.id)}
                    onChange={(e) => setChecked((current) => (
                      e.target.checked ? [...current, line.id] : current.filter((id) => id !== line.id)
                    ))}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {line.labour_confidence != null && line.labour_confidence < 70 && <FlagIcon color="warning" sx={{ fontSize: 14, mr: 0.5 }} />}
                  {displayTagRef(line) || '—'}
                </TableCell>
                <TableCell>{line.description}</TableCell>
                <TableCell sx={{ color: suggestedFields(line).includes('mounting_method') ? SUGGESTED_COLOR : 'inherit' }}>
                  {mountingLabel(mountingMethods, line.mounting_method)}
                  {Boolean(line.labour_inputs?.mounting_not_required) ? ' · Not req.' : ''}
                </TableCell>
                <TableCell sx={{ color: suggestedFields(line).includes('mounting_location') ? SUGGESTED_COLOR : 'inherit' }}>{line.mounting_location || '—'}</TableCell>
                <TableCell align="right" sx={{ color: suggestedFields(line).includes('connections') ? SUGGESTED_COLOR : 'inherit' }}>
                  {line.labour_inputs?.no_wire ? '0 · no wire' : (line.connections ?? '—')}
                </TableCell>
                <TableCell align="right">{line.labour_hrs != null ? line.labour_hrs.toFixed(3) : '—'}</TableCell>
                <TableCell align="right">{line.labour_confidence != null ? `${line.labour_confidence}%` : '—'}</TableCell>
                <TableCell align="center">
                  <Tooltip title="Labour details">
                    <IconButton size="small" color={line.mounting_method && line.mounting_location ? 'secondary' : 'warning'} onClick={() => openPopup(line, 'labour')}>
                      <HandymanIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title={line.crawl_json ? `Crawl ${line.crawl_confidence ?? '—'}%` : 'No crawl data'}>
                    <IconButton size="small" color={line.crawl_json ? 'success' : 'default'} onClick={() => openPopup(line, 'crawl')}>
                      <TravelExploreIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={popup === 'labour'} onClose={() => setPopup(null)} fullWidth maxWidth="sm">
        <DialogTitle>Labour · {(selected && (displayTagRef(selected) || selected.vendor_item_no)) || 'Line'}</DialogTitle>
        <DialogContent>
          {selected && (
            <Box sx={{ pt: 1 }}>
              {excelImport && (
                <TextField
                  fullWidth
                  size="small"
                  label="Tag / Ref"
                  inputProps={{ maxLength: 32 }}
                  value={displayTagRef({ ...selected, ...draft })}
                  disabled={locked}
                  sx={{ mb: 1 }}
                  onChange={(e) => setDraft({ ...draft, tag: e.target.value.slice(0, 32), ref: e.target.value.slice(0, 32) })}
                  helperText="Schematic tag only. ERP PP# and Vendor Item No. are locked keys."
                />
              )}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                <FormControl fullWidth size="small" disabled={locked || Boolean(draft.labour_inputs?.mounting_not_required)}>
                  <InputLabel>Mounting</InputLabel>
                  <Select
                    value={mountingValue(mountingMethods, draft.mounting_method)}
                    label="Mounting"
                    disabled={locked || Boolean(draft.labour_inputs?.mounting_not_required)}
                    onChange={(e) => {
                      const method = mountingMethods.find((item) => item.key === e.target.value);
                      const blankConn = draft.connections == null && (draft.labour_inputs?.conn_20a == null || draft.labour_inputs?.conn_20a === '');
                      setDraft({
                        ...draft,
                        mounting_method: e.target.value,
                        ...(method?.no_connections && blankConn ? { connections: 0, labour_inputs: { ...draft.labour_inputs, conn_20a: 0, no_wire: true } } : {}),
                      });
                    }}
                    sx={{ color: suggestedFields(draft).includes('mounting_method') ? SUGGESTED_COLOR : 'inherit' }}
                  >
                    {mountingMethods.map((m) => <MenuItem key={m.key} value={m.key}>{m.label}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControlLabel
                  sx={{ whiteSpace: 'nowrap', mr: 0 }}
                  control={(
                    <Checkbox
                      size="small"
                      disabled={locked}
                      checked={Boolean(draft.labour_inputs?.mounting_not_required)}
                      onChange={(e) => setDraft({
                        ...draft,
                        labour_inputs: { ...draft.labour_inputs, mounting_not_required: e.target.checked },
                      })}
                    />
                  )}
                  label="Not req."
                />
              </Box>
              <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                <InputLabel>Location</InputLabel>
                <Select
                  value={draft.mounting_location || ''}
                  label="Location"
                  disabled={locked}
                  onChange={(e) => setDraft({ ...draft, mounting_location: e.target.value })}
                  sx={{ color: suggestedFields(draft).includes('mounting_location') ? SUGGESTED_COLOR : 'inherit' }}
                >
                  {locations.map((loc) => <MenuItem key={loc} value={loc}>{loc}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControlLabel
                sx={{ mb: 1 }}
                control={(
                  <Checkbox
                    size="small"
                    disabled={locked}
                    checked={Boolean(draft.labour_inputs?.no_wire)}
                    onChange={(e) => setDraft({
                      ...draft,
                      connections: e.target.checked ? 0 : draft.connections,
                      labour_inputs: { ...draft.labour_inputs, no_wire: e.target.checked, conn_20a: e.target.checked ? 0 : draft.labour_inputs?.conn_20a },
                    })}
                  />
                )}
                label="No wire"
              />
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField size="small" type="number" label="20A" value={draft.labour_inputs?.conn_20a ?? draft.connections ?? ''} disabled={locked || Boolean(draft.labour_inputs?.no_wire)} onChange={(e) => setDraft({ ...draft, connections: Number(e.target.value), labour_inputs: { ...draft.labour_inputs, conn_20a: Number(e.target.value) } })} sx={{ flex: 1, '& .MuiInputBase-input': { color: suggestedFields(draft).includes('connections') ? SUGGESTED_COLOR : 'inherit' } }} />
                <TextField size="small" type="number" label="50A" value={draft.labour_inputs?.conn_50a ?? ''} disabled={locked} onChange={(e) => setDraft({ ...draft, labour_inputs: { ...draft.labour_inputs, conn_50a: Number(e.target.value) } })} sx={{ flex: 1 }} />
                <TextField size="small" type="number" label="167A" value={draft.labour_inputs?.conn_167a ?? ''} disabled={locked} onChange={(e) => setDraft({ ...draft, labour_inputs: { ...draft.labour_inputs, conn_167a: Number(e.target.value) } })} sx={{ flex: 1 }} />
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField size="small" type="number" label=">167A" value={draft.labour_inputs?.conn_gt167 ?? ''} disabled={locked} onChange={(e) => setDraft({ ...draft, labour_inputs: { ...draft.labour_inputs, conn_gt167: Number(e.target.value) } })} sx={{ flex: 1 }} />
                <TextField size="small" type="number" label="Twisted" value={draft.labour_inputs?.twisted ?? ''} disabled={locked} onChange={(e) => setDraft({ ...draft, labour_inputs: { ...draft.labour_inputs, twisted: Number(e.target.value) } })} sx={{ flex: 1 }} />
                <TextField size="small" type="number" label="Multicore" value={draft.labour_inputs?.multicore ?? ''} disabled={locked} onChange={(e) => setDraft({ ...draft, labour_inputs: { ...draft.labour_inputs, multicore: Number(e.target.value) } })} sx={{ flex: 1 }} />
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>Flags</Typography>
              {flagCats.map((flag) => (
                <FormControlLabel
                  key={flag.key}
                  control={
                    <Checkbox
                      size="small"
                      checked={(draft.flags || []).includes(flag.label)}
                      disabled={locked}
                      onChange={(e) => {
                        const current = draft.flags || [];
                        setDraft({
                          ...draft,
                          flags: e.target.checked ? [...current, flag.label] : current.filter((item) => item !== flag.label),
                        });
                      }}
                    />
                  }
                  label={flag.label}
                />
              ))}
              <Typography variant="body2">Method A {selected.labour_method_a?.toFixed(3) ?? '—'} hrs</Typography>
              <Typography variant="body2">Method B {selected.labour_method_b?.toFixed(3) ?? '—'} hrs</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => selected && openSchematic(selected)} >Schematic</Button>
          <Box sx={{ flex: 1 }} />
          <Button size="small" onClick={() => setPopup(null)}>Close</Button>
          <Button size="small" variant="contained" disabled={locked || busy} onClick={handleSaveLabour}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={popup === 'crawl'} onClose={() => setPopup(null)} fullWidth maxWidth="md">
        <DialogTitle>
          Crawl · {selected?.vendor_item_no || selected?.tag || 'Line'}
          {selected?.crawl_confidence != null ? ` · ${selected.crawl_confidence}%` : ''}
        </DialogTitle>
        <DialogContent>
          {selected?.erp_catalog && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Catalog manufacturer {selected.erp_catalog.manufacturer || selected.erp_catalog.manufacturer_code || '—'}
              {selected.erp_catalog.customer_part_number ? ` · Customer PN ${selected.erp_catalog.customer_part_number}` : ''}
            </Alert>
          )}
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={16}
            value={selected?.crawl_json ? JSON.stringify(selected.crawl_json, null, 2) : 'No crawl data'}
            InputProps={{ readOnly: true }}
            sx={{ mt: 1, fontFamily: 'monospace' }}
          />
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => selected && openSchematic(selected)} >Schematic</Button>
          <Box sx={{ flex: 1 }} />
          <Button size="small" onClick={() => setPopup(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(schematic)} onClose={() => setSchematic(null)} fullWidth maxWidth="lg">
        <DialogTitle>Schematic · {schematic?.tag || 'TAG'}</DialogTitle>
        <DialogContent>
          {schematic && selected && (
            <SchematicViewer
              quoteId={quote.id}
              lineId={selected.id}
              meta={schematic}
              page={schematicPage}
              onPageChange={setSchematicPage}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setSchematic(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LabourView;
